import { ActualDurationSchema, type PermissionRequest, ReassignApproverSchema } from '@gestion-permisos/contracts';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { describedBy, Field, firstForbidden, INPUT, isStale, Notice } from '../admin/admin-ui.js';
import { OPTIONS_PAGE_SIZE, useEmployeeOptions } from '../admin/people-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { ChoiceList } from '../components/choice-list.js';
import { Card, CARD_SURFACE, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { formatHours } from '../requests/format.js';
import { RequestDocument } from '../requests/request-document.js';
import { type FieldRow, RequestFields, RequestHistory } from '../requests/request-details.js';
import { useCancelRequest, useReassignApprover, useRequest, useRequestHistory, useSetActualDuration } from '../requests/requests-api.js';
import { StatusBadge } from '../requests/status-badge.js';
import { NOT_A_REQUEST } from './request-detail-page.js';

const CONFLICT_NOTICE = 'La solicitud cambió mientras la revisabas. Se muestra su estado actual.';

/**
 * Detalle administrativo (W9): detalle e historial con los nombres de H-01, y tres acciones que solo están
 * aquí (no en `/solicitudes/:id`): cancelar, registrar la duración real y reasignar el aprobador. Ofrecerlas
 * según el estado es **solo presentación**: la API decide (403, 409, 412 o 422 se muestran tal como llegan).
 * Un 412 en cualquiera de ellas vuelve a leer la solicitud. Con el aspecto del detalle propio y de la
 * revisión del aprobador (modelo: `src/pages/admin-request-details-page.tsx`): cabecera con la insignia, una
 * tarjeta «Acciones» con los botones que abren cada acción en una ventana (`Modal`, una a la vez), y datos,
 * documento e historial en tarjetas. Cerrar una ventana no llama a la API.
 */
export function AdminRequestPage() {
  const { id = '' } = useParams();
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_REQUEST} />;
  return <AdminRequest id={id} />;
}

type Action = 'cancel' | 'duration' | 'reassign';

function AdminRequest({ id }: { id: string }) {
  const request = useRequest(id);
  const history = useRequestHistory(id);
  const cancel = useCancelRequest(id);
  const duration = useSetActualDuration(id);
  const reassign = useReassignApprover(id);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<Action | undefined>(undefined);

  const error = cancel.error ?? duration.error ?? reassign.error;
  const denied = firstForbidden(request.error, error);
  if (denied) return <ApiErrorView error={denied} />;
  if (request.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (request.isError) return <ApiErrorView error={request.error} />;
  const data = request.data;

  // Qué acciones se ofrecen según el estado (solo presentación: la API decide).
  const available: Record<Action, boolean> = {
    cancel: data.status === 'DRAFT' || data.status === 'PENDING_APPROVAL',
    reassign: data.status === 'PENDING_APPROVAL',
    duration: data.status === 'APPROVED',
  };
  // Una ventana a la vez, y solo si su acción sigue disponible: si un 412 o un 409 cambian el estado, se cierra.
  const shown = open !== undefined && available[open] ? open : undefined;

  /** Abrir una acción limpia el resultado de todas; la ventana anterior se cierra sin llamar a la API. */
  const openAction = (action: Action) => {
    setNotice(undefined);
    cancel.reset();
    duration.reset();
    reassign.reset();
    setOpen(action);
  };
  const close = () => setOpen(undefined);
  const handlers = (done: (saved: PermissionRequest) => string) => ({
    onSuccess: (saved: PermissionRequest) => {
      setOpen(undefined);
      setNotice(done(saved));
    },
    onError: (failure: unknown) => setNotice(isStale(failure) ? CONFLICT_NOTICE : undefined),
  });

  // Avisos y errores: dentro de la ventana mientras está abierta; si no (o si el estado cambió y se cerró), en
  // la página, para que no se pierdan. Nunca en los dos sitios.
  const feedback =
    notice !== undefined || error !== null ? (
      <div className="space-y-3">
        <Notice text={notice} />
        {error !== null ? <ProblemMessage error={error} /> : null}
      </div>
    ) : null;

  const people: FieldRow[] = [
    ['Solicitante', data.employee.fullName],
    ...(data.approver !== null ? ([['Aprobador', data.approver.fullName]] as FieldRow[]) : []),
    ['Tipo de permiso', data.permissionType.name],
  ];

  return (
    <div className="space-y-6 lg:px-6 lg:py-4">
      <Link to="/administracion/solicitudes" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a solicitudes
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {data.requestNumber} <StatusBadge status={data.status} tokens />
          </span>
        }
        description="Cancela, registra la duración real o reasigna el aprobador."
      />

      {shown === undefined ? feedback : null}

      {available.cancel || available.reassign || available.duration ? (
        <section aria-labelledby="acciones" className={`space-y-3 p-5 ${CARD_SURFACE}`}>
          <h2 id="acciones" className="text-base leading-none font-semibold tracking-tight">
            Acciones
          </h2>
          <div className="flex flex-wrap gap-2">
            {available.cancel ? (
              <button type="button" className={OUTLINE} onClick={() => openAction('cancel')}>
                Cancelar solicitud
              </button>
            ) : null}
            {available.reassign ? (
              <button type="button" className={OUTLINE} onClick={() => openAction('reassign')}>
                Reasignar aprobador
              </button>
            ) : null}
            {available.duration ? (
              <button type="button" className={OUTLINE} onClick={() => openAction('duration')}>
                Registrar duración real
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {shown === 'cancel' ? (
        <Modal
          title="¿Cancelar esta solicitud?"
          description="Acción administrativa excepcional. El registro se conservará y su estado cambiará a Cancelada. Esta acción no elimina la solicitud ni se puede revertir."
          onClose={close}
        >
          <CancelForm
            pending={cancel.isPending}
            feedback={feedback}
            onBack={close}
            onCancel={(comment) => {
              setNotice(undefined);
              cancel.mutate({ version: data.version, comment }, handlers(() => 'Solicitud cancelada.'));
            }}
          />
        </Modal>
      ) : null}
      {shown === 'duration' ? (
        <Modal
          title={data.actualHours === null ? 'Registrar tiempo real' : 'Modificar tiempo real'}
          description="La duración solicitada no se modifica. La duración real se usa en la contabilidad de horas aprobadas."
          onClose={close}
        >
          <DurationForm
            // Una versión nueva (tras un 412) recarga el formulario; la ventana sigue abierta.
            key={data.version}
            request={data}
            pending={duration.isPending}
            feedback={feedback}
            onBack={close}
            onSave={(actualHours) => {
              setNotice(undefined);
              // El texto de éxito usa la duración que devuelve la API, no la escrita.
              duration.mutate(
                { version: data.version, body: { actualHours } },
                handlers((saved) => `Duración real registrada: ${saved.actualHours === null ? '—' : formatHours(saved.actualHours)}.`),
              );
            }}
          />
        </Modal>
      ) : null}
      {shown === 'reassign' ? (
        <Modal title="Reasignar aprobador" description="No se modificará la jerarquía del empleado." onClose={close}>
          <ApproverForm
            key={data.version}
            request={data}
            pending={reassign.isPending}
            feedback={feedback}
            onBack={close}
            onReassign={(approverId) => {
              setNotice(undefined);
              reassign.mutate({ version: data.version, body: { approverId } }, handlers(() => 'Aprobador reasignado.'));
            }}
            onUnchanged={() => {
              reassign.reset();
              setNotice('Elige a otra persona como aprobador: no hay cambios que enviar.');
            }}
          />
        </Modal>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Datos del permiso</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestFields request={data} leading={people} className="" />
        </CardContent>
      </Card>
      <RequestDocument request={data} editable={false} className={`p-5 ${CARD_SURFACE}`} />
      <Card>
        <CardHeader>
          <CardTitle id="historial">Historial</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isPending ? <output className="block text-muted-foreground">Cargando el historial…</output> : null}
          {history.isError ? <ApiErrorView error={history.error} /> : null}
          {history.isSuccess ? <RequestHistory events={history.data} showNames tokens /> : null}
        </CardContent>
      </Card>
    </div>
  );
}

const PRIMARY_BUTTON = buttonClasses();
const OUTLINE = buttonClasses('outline');

/** Cancelar (`POST …/cancel` con `If-Match` e `Idempotency-Key`); el comentario es opcional. */
function CancelForm({
  pending,
  feedback,
  onBack,
  onCancel,
}: {
  pending: boolean;
  feedback: ReactNode;
  onBack: () => void;
  onCancel: (comment: string) => void;
}) {
  const [comment, setComment] = useState('');
  return (
    <form
      aria-label="Cancelar la solicitud"
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        onCancel(comment);
      }}
    >
      {feedback}
      <Field id="admin-cancel-comment" label="Comentario de la cancelación (opcional)">
        <textarea id="admin-cancel-comment" rows={3} maxLength={2000} value={comment} onChange={(event) => setComment(event.target.value)} className={INPUT} />
      </Field>
      <ModalFooter>
        <button type="button" disabled={pending} className={OUTLINE} onClick={onBack}>
          Volver
        </button>
        <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
          Confirmar cancelación
        </button>
      </ModalFooter>
    </form>
  );
}

/**
 * Duración real (`PUT …/actual-duration`). Se valida con `ActualDurationSchema` (> 0, ≤ 24, como mucho dos
 * decimales) antes de enviar. No se calcula a partir del horario: el campo empieza con la duración registrada,
 * o vacío. La duración solicitada se muestra tal como la da la API.
 */
function DurationForm({
  request,
  pending,
  feedback,
  onBack,
  onSave,
}: {
  request: PermissionRequest;
  pending: boolean;
  feedback: ReactNode;
  onBack: () => void;
  onSave: (hours: number) => void;
}) {
  const [text, setText] = useState(request.actualHours === null ? '' : String(request.actualHours));
  const [error, setError] = useState<string | undefined>(undefined);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = text.trim().replace(',', '.');
    const parsed = ActualDurationSchema.safeParse({ actualHours: trimmed === '' ? Number.NaN : Number(trimmed) });
    if (!parsed.success) {
      setError('Escribe una duración mayor que 0 y de hasta 24 horas, con dos decimales como máximo.');
      return;
    }
    setError(undefined);
    onSave(parsed.data.actualHours);
  };
  return (
    <form noValidate aria-label="Duración real" className="space-y-3" onSubmit={submit}>
      {feedback}
      <dl className="rounded-md border border-border bg-muted p-3">
        <dt className="text-sm text-muted-foreground">Duración solicitada</dt>
        <dd className="mt-1 font-medium">{formatHours(request.requestedHours)}</dd>
      </dl>
      <Field id="admin-actual-hours" label="Duración real (horas)" error={error}>
        <input id="admin-actual-hours" inputMode="decimal" value={text} onChange={(event) => setText(event.target.value)} className={INPUT} {...describedBy('admin-actual-hours', error)} />
      </Field>
      <ModalFooter>
        <button type="button" disabled={pending} className={OUTLINE} onClick={onBack}>
          Cancelar
        </button>
        <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
          Guardar
        </button>
      </ModalFooter>
    </form>
  );
}

/**
 * Reasignar el aprobador (`POST …/approver`). Opciones: una página de 100 de `GET /employees`, que solo se pide
 * mientras esta ventana está abierta; si hay más, se indica. El aprobador actual se muestra aunque no esté en
 * esa página. Sin elegir a otra persona no hay petición. Si la persona es válida (activa, con correo, distinta
 * del solicitante) lo decide la API.
 */
function ApproverForm({
  request,
  pending,
  feedback,
  onBack,
  onReassign,
  onUnchanged,
}: {
  request: PermissionRequest;
  pending: boolean;
  feedback: ReactNode;
  onBack: () => void;
  onReassign: (approverId: string) => void;
  onUnchanged: () => void;
}) {
  const options = useEmployeeOptions();
  const [selected, setSelected] = useState(request.approverId ?? '');
  const items = options.data?.items ?? [];
  const current = request.approver;
  const missingCurrent = current !== null && !items.some((person) => person.id === current.id);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = ReassignApproverSchema.safeParse({ approverId: selected });
    if (!parsed.success || parsed.data.approverId === request.approverId) {
      onUnchanged();
      return;
    }
    onReassign(parsed.data.approverId);
  };

  return (
    <form noValidate aria-label="Reasignar el aprobador" className="space-y-3" onSubmit={submit}>
      {feedback}
      <Field id="admin-approver" label="Aprobador">
        <ChoiceList
          id="admin-approver"
          value={selected}
          options={[
            ...(current === null ? [{ value: '', label: 'Sin aprobador' }] : []),
            ...(missingCurrent ? [{ value: current.id, label: current.fullName }] : []),
            ...items.map((person) => ({ value: person.id, label: person.fullName })),
          ]}
          onChange={setSelected}
          className={INPUT}
        />
      </Field>
      {options.data && options.data.total > OPTIONS_PAGE_SIZE ? (
        <p className="text-sm text-amber-800">
          Hay {options.data.total} empleados; solo se muestran los {OPTIONS_PAGE_SIZE} primeros.
        </p>
      ) : null}
      {options.error ? <ProblemMessage error={options.error} /> : null}
      <ModalFooter>
        <button type="button" disabled={pending} className={OUTLINE} onClick={onBack}>
          Cancelar
        </button>
        <button type="submit" disabled={pending} className={PRIMARY_BUTTON}>
          Confirmar
        </button>
      </ModalFooter>
    </form>
  );
}
